import get from 'lodash.get';
import pick from 'lodash.pick';
import { getCheckoutParam } from '../helpers/checkoutRedirects';
import { getUserEmail } from '../helpers/checkoutUser';
import { getUserGeoInfo } from 'dibs-regional-info/exports/regionalInfoHelpers';
import {
    shippingFields,
    getDefaultCountry,
    isSameRegion,
    getRegistrationErrors,
} from '../helpers/checkoutForms';
import { STATES } from '../constants/checkoutConstants';
import { graphql, readInlineData } from 'relay-runtime';
import {
    addressSelectedAction,
    navigateDeliveryAction,
    transactionBuyerChangedAction,
    shippingCompletedAction,
    registrationErrorAction,
    emptyShippingAddressAction,
    navigatePaymentAction,
} from '../actions/commonActions';
import { getGuestUserId } from '../helpers/checkoutGuestUserToken';

const getShippingState = state => state.shippingState;

const isCurrentState = (shippingState, state) => shippingState === getShippingState(state);

const getPreFillAddress = preCheckoutTransaction => {
    const { shippingAddress } = get(preCheckoutTransaction, 'shipment') || {};
    return shippingAddress
        ? {
              country: shippingAddress.country,
              city: shippingAddress.city,
              state: shippingAddress.state,
              zipCode: shippingAddress.zipCode,
          }
        : {};
};

const getNewShippingForm = state => {
    const regionalInfo = { country: state.defaultCountry, zipCode: state.defaultZipCode };
    if (Object.keys(state.preFilledAddress).length) {
        return {
            ...regionalInfo,
            ...state.preFilledAddress,
            serviceId: null,
        };
    }
    return regionalInfo;
};

const initializeRegistrationForm = () => {
    const email = getUserEmail();
    return {
        registrationForm: { email },
        wasEmailPrefilled: !!email,
    };
};

//getters
export const isAdding = state => isCurrentState(STATES.STATE_NEW, state);
export const isEditing = state => isCurrentState(STATES.STATE_EDIT, state);
export const canUsePreFilledAddress = state => !!state?.preFilledAddress?.serviceId;
export const showPreFilledAddressNotice = state => {
    const { preFilledAddress, hasRequestedQuote } = state;
    return !!hasRequestedQuote && !preFilledAddress?.serviceId;
};

const initializeFormState = state => ({
    ...state,
    shippingState: STATES.STATE_SAVED,
    newShippingForm: getGuestUserId() ? state.newShippingForm : getNewShippingForm(state),
    editShippingForm: {},
    registrationForm: {},
    registrationErrors: {},
    shouldScrolledToPreFilledAddress: false,
    isShippingProcessing: false,
    showTaxWarning: false,
    showAddressFormErrors: false,
});

const initialState = {
    shippingState: STATES.STATE_NEW,
    preFilledAddress: {},
    newShippingForm: {},
    editShippingForm: {},
    ...initializeRegistrationForm(),
    registrationErrors: {},
    lastValidShippingAddressId: null,
    hasRequestedQuote: false,
    isShippingProcessing: false,
    defaultZipCode: null,
    defaultCountry: null,
    showTaxWarning: false,
    showAddressFormErrors: false,
    previewZipCode: null,
    isSelectedAddressValid: true,
};

export function checkoutShippingReducer(state = initialState, action = {}) {
    switch (action.type) {
        case 'CHECKOUT_BUYER_ITEM_VIEWER_UPDATE': {
            // should run at most 2 times
            // 1) first checkout page load, with or without a buyer based on log in status
            // 2) when a logged out user logs in
            const {
                buyer: buyerRef,
                emailUser: emailUserRef,
                preCheckoutTransaction: preCheckoutTransactionRef,
                viewer: viewerRef,
            } = action.payload;
            const viewer = readInlineData(
                graphql`
                    fragment checkoutShippingReducer_viewer on Viewer @inline {
                        regionalInfo @skip(if: $hasSessionUserGeoInfo) {
                            zipCode
                            countryCode
                        }
                    }
                `,
                viewerRef
            );
            const buyer = readInlineData(
                graphql`
                    fragment checkoutShippingReducer_buyer on User @inline {
                        profile {
                            country
                        }
                        firstValidShippingAddress {
                            serviceId
                            priceBookName
                        }
                        addressBook {
                            personalAddress {
                                serviceId
                                validation {
                                    buyerShipping {
                                        valid
                                    }
                                }
                                priceBookName
                            }
                            addressList {
                                serviceId
                                firstName
                                lastName
                                company
                                line1
                                line2
                                country
                                state
                                city
                                zipCode
                                phoneNumber
                                validation {
                                    buyerShipping {
                                        valid
                                    }
                                }
                                priceBookName
                            }
                        }
                    }
                `,
                buyerRef
            );

            const preCheckoutTransaction = readInlineData(
                graphql`
                    fragment checkoutShippingReducer_preCheckoutTransaction on Transaction @inline {
                        hasPartialShippingAddress
                        transactionType
                        shipment {
                            shippingAddress {
                                sourceId
                                country
                                city
                                state
                                zipCode
                                priceBookName
                            }
                        }
                        mostRecentOffer {
                            priceBookName
                        }
                        isAuctionOrderType
                    }
                `,
                preCheckoutTransactionRef
            );

            const emailUser = readInlineData(
                graphql`
                    fragment checkoutShippingReducer_emailUser on User @inline {
                        profile {
                            email
                        }
                    }
                `,
                emailUserRef
            );

            const { regionalInfo } = viewer || {};
            const preCheckoutAddressId =
                preCheckoutTransaction?.shipment?.shippingAddress?.sourceId;
            const { addressBook } = buyer || {};
            const { addressList, personalAddress } = addressBook || {};
            const { serviceId: personalAddressId, validation: personalAddressValidation } =
                personalAddress || {};
            const firstValidShippingAddressId =
                buyer?.firstValidShippingAddress?.serviceId || getCheckoutParam('shippingAddress');
            const addressListAddress = (addressList || []).find(
                ({ serviceId, validation }) =>
                    serviceId === preCheckoutAddressId && validation?.buyerShipping?.valid
            );
            const shippingAddressId =
                addressListAddress?.serviceId || personalAddressId || firstValidShippingAddressId;
            const hasPartialShippingAddress = preCheckoutTransaction?.hasPartialShippingAddress;
            const isPersonalAddressValid = personalAddressValidation?.buyerShipping?.valid;
            const regionalAddress = getUserGeoInfo() || (regionalInfo && regionalInfo[0]) || {};
            const selectedAddress =
                (addressList || []).find(({ serviceId }) => serviceId === shippingAddressId) || {};

            let shippingState = STATES.STATE_NEW;
            if (
                shippingAddressId &&
                personalAddressId === shippingAddressId &&
                !isPersonalAddressValid
            ) {
                shippingState = STATES.STATE_EDIT;
            } else if (shippingAddressId && !hasPartialShippingAddress) {
                shippingState = STATES.STATE_SAVED;
            }

            const newState = {
                ...state,
                defaultCountry:
                    selectedAddress.country ||
                    buyer?.profile?.country ||
                    regionalAddress.countryCode ||
                    getDefaultCountry(),
                defaultZipCode: regionalAddress.zipCode || '',
                previewZipCode: selectedAddress.zipCode || regionalAddress.zipCode || '',
                shippingState,
                preFilledAddress: getPreFillAddress(preCheckoutTransaction),
                lastValidShippingAddressId: isPersonalAddressValid
                    ? personalAddressId
                    : firstValidShippingAddressId,
                shouldScrolledToPreFilledAddress: hasPartialShippingAddress,
                isSelectedAddressValid:
                    (personalAddressId === shippingAddressId && isPersonalAddressValid) ||
                    personalAddressId !== shippingAddressId,
                registrationForm: {
                    ...state.registrationForm,
                    email: emailUser?.profile?.email
                        ? emailUser.profile.email
                        : state.registrationForm?.email,
                },
            };

            return {
                ...newState,
                newShippingForm: getGuestUserId()
                    ? state.newShippingForm
                    : getNewShippingForm(newState, !!buyer),
                editShippingForm:
                    shippingState === STATES.STATE_EDIT && selectedAddress
                        ? pick(selectedAddress, shippingFields)
                        : {},
            };
        }
        case 'CHANGE_COUNTRY': {
            return {
                ...state,
                defaultCountry: action.country,
                previewZipCode: action.zip,
            };
        }
        case transactionBuyerChangedAction.toString(): {
            return {
                ...state,
                // don't reset shipping form if guest user.
                // guest users will not see an address list.
                // when a guest user clicks to change their address we want
                // them to create a new address with the previous one prefilled in the form
                newShippingForm: getGuestUserId()
                    ? state.newShippingForm
                    : getNewShippingForm(state),
            };
        }
        case 'CHECKOUT_PANE_CHANGED': {
            const { hasShippingAddresses } = action;
            return {
                ...state,
                shippingState:
                    hasShippingAddresses && !getGuestUserId()
                        ? STATES.STATE_SAVED
                        : STATES.STATE_NEW,
            };
        }
        case 'CHECKOUT_SET_SHIPPING_STATE': {
            const { shippingState, shippingAddress } = action;
            return {
                ...state,
                shippingState,
                editShippingForm:
                    shippingState === STATES.STATE_EDIT && shippingAddress
                        ? pick(shippingAddress, shippingFields)
                        : {},
            };
        }
        case 'CHECKOUT_NEW_SHIPPING_ADDRESS': {
            return {
                ...state,
                shippingState: STATES.STATE_NEW,
                editShippingForm: {},
            };
        }
        case addressSelectedAction.toString(): {
            const { preFilledAddress } = state;
            const { shippingState, shippingAddress, usePreFilledAddress, isEditingPartial } =
                action.payload;

            const hasSameRegion =
                preFilledAddress &&
                shippingAddress &&
                isSameRegion(preFilledAddress, shippingAddress);
            const newState = {
                ...state,
                shippingState,
                preFilledAddress: hasSameRegion ? {} : preFilledAddress,
                editShippingForm:
                    shippingState === STATES.STATE_EDIT && shippingAddress
                        ? pick(shippingAddress, shippingFields)
                        : {},
                lastValidShippingAddressId:
                    usePreFilledAddress || isEditingPartial
                        ? state.lastValidShippingAddressId
                        : shippingAddress?.serviceId || null,
                shouldScrolledToPreFilledAddress: false,
                defaultCountry: shippingAddress?.country || null,
                previewZipCode: shippingAddress?.zipCode || null,
                isSelectedAddressValid: !isEditingPartial,
            };
            return {
                ...newState,
                newShippingForm:
                    shippingState === STATES.STATE_NEW
                        ? getNewShippingForm(newState)
                        : newState.newShippingForm,
            };
        }
        case emptyShippingAddressAction.toString(): {
            return {
                ...state,
                shippingState: STATES.STATE_NEW,
                newShippingForm: getNewShippingForm(state),
                editShippingForm: {},
                lastValidShippingAddressId: null,
            };
        }
        case registrationErrorAction.toString(): {
            const { errors } = action.payload;
            return {
                ...state,
                registrationErrors: errors,
            };
        }
        case 'CHECKOUT_UPDATE_NEW_SHIPPING_FORM': {
            const { field, value } = action;
            return {
                ...state,
                newShippingForm: { ...state.newShippingForm, [field]: value },
            };
        }
        case 'CHECKOUT_EDIT_SHIPPING_FORM': {
            const { field, value } = action;
            return {
                ...state,
                editShippingForm: { ...state.editShippingForm, [field]: value },
            };
        }
        case 'CHECKOUT_UPDATE_REGISTRATION_FORM': {
            const { field, value } = action;
            const registrationForm = { ...state.registrationForm, [field]: value };
            const { errors: registrationErrors } = getRegistrationErrors({
                form: registrationForm,
            });
            return {
                ...state,
                registrationForm,
                registrationErrors,
            };
        }
        case 'CHECKOUT_CANCEL_SHIPPING_FORM_EDIT': {
            return {
                ...state,
                editShippingForm: {},
                showAddressFormErrors: false,
            };
        }
        case 'CHECKOUT_CANCEL_NEW_SHIPPING_FORM': {
            return {
                ...state,
                newShippingForm: getNewShippingForm(state),
                shouldScrolledToPreFilledAddress: false,
            };
        }
        case 'CHECKOUT_SHIPPING_SAVED': {
            //checkout:shipping:saved TODO see if this was getting used cause it looks dead
            return initializeFormState(state);
        }
        case shippingCompletedAction.toString(): {
            return initializeFormState(state);
        }
        case navigateDeliveryAction.toString(): {
            return initializeFormState(state);
        }
        case navigatePaymentAction.toString(): {
            return initializeFormState(state);
        }
        case 'CHECKOUT_SET_SHOW_TAX_WARINING': {
            const { showTaxWarning } = action;
            return {
                ...state,
                showTaxWarning,
            };
        }
        case 'CHECKOUT_SHOW_ADDRESS_FORM_ERRORS': {
            const { showAddressFormErrors } = action;
            return {
                ...state,
                showAddressFormErrors,
            };
        }
    }
    return state;
}
